import { useState, useEffect, useCallback } from 'react';
import { useCustom, useCustomMutation } from '@refinedev/core';

interface FeatureTour {
  tourId: string;
  completedAt: string;
}

/**
 * Hook pour gerer les tours guides par page.
 * Verifie si le tour a deja ete vu, et l'affiche automatiquement si non.
 */
export function usePageTour(tourId: string) {
  const [isActive, setIsActive] = useState(false);

  const { data, isLoading } = useCustom<{ data: FeatureTour[] }>({
    url: '/api/v1/me/feature-tours',
    method: 'get',
  });

  const { mutate: markComplete } = useCustomMutation();

  // La structure de reponse varie : data.data.data (array) ou data.data (array/objet)
  const raw = data?.data;
  const toursRaw = (raw as Record<string, unknown>)?.data ?? raw;
  const tours: FeatureTour[] = Array.isArray(toursRaw) ? toursRaw : [];
  const hasSeenTour = tours.some((t) => t.tourId === tourId);

  // Lance automatiquement le tour si non vu
  useEffect(() => {
    if (!isLoading && !hasSeenTour) {
      const timer = setTimeout(() => setIsActive(true), 600);
      return () => clearTimeout(timer);
    }
  }, [isLoading, hasSeenTour]);

  const completeTour = useCallback(() => {
    setIsActive(false);
    markComplete({
      url: '/api/v1/me/feature-tours',
      method: 'post',
      values: { tourId },
    });
  }, [tourId, markComplete]);

  const startTour = useCallback(() => {
    setIsActive(true);
  }, []);

  const skipTour = useCallback(() => {
    setIsActive(false);
    markComplete({
      url: '/api/v1/me/feature-tours',
      method: 'post',
      values: { tourId },
    });
  }, [tourId, markComplete]);

  return {
    isActive,
    isLoading,
    hasSeenTour,
    startTour,
    completeTour,
    skipTour,
  };
}
